import { NgModule, ModuleWithProviders, ErrorHandler } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MediaMarshaller } from 'ngx-flexible-layout';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha-2';
import { AuthGuardService } from 'src/app/guards/auth-guard.service';
import { ProductionGuardService } from 'src/app/guards/production-guard.service';
import { StagingGuardService } from 'src/app/guards/staging-guard.service';
import { AWSService } from 'src/app/shared/services/aws.service';
import { GlobalErrorHandler } from 'src/app/global.error-config';
import { OrderByPipe } from 'src/app/shared/pipes/order-by.pipe';
import { currentOrUpcomingTeamsSummaryPipe } from 'src/app/shared/pipes/currentOrUpcomingTeamsSummary.pipe';
import { SearchModeService } from 'src/app/shared/services/search-mode.service';
import { FilterByPipe } from 'src/app/shared/pipes/filter-by.pipe';
import { SearchBarComponent } from 'src/app/shared/components/search-bar/search-bar.component';
import { PreviewApplicationModalComponent } from 'src/app/shared/components/preview-application-modal/preview-application-modal.component';
import { DeclineReasonModalComponent } from 'src/app/shared/components/decline-reason-modal/decline-reason-modal.component';
import { DeletePersonModalComponent } from 'src/app/shared/components/delete-person-modal/delete-person-modal.component';
import { DeletePersonWorkspaceModalComponent } from './components/delete-person-workspace-modal/delete-person-workspace-modal.component';
import { DeleteWorkspaceModalComponent } from './components/delete-workspace-modal/delete-workspace-modal.component';
import { TagService } from 'src/app/shared/services/tag.service';
import { CheckboxGroup } from 'src/app/shared/components/checkbox-group/checkbox-group.component';
import { TeamHeaderComponent } from 'src/app/teams/team-header/team-header.component';
import { TeamsNavComponent } from 'src/app/teams/teams-nav/teams-nav.component';
import { PositionDetailsComponent } from 'src/app/teams/position-details/position-details.component';
import { TeamAddComponent } from 'src/app/teams/team-add/team-add.component';
import { TeamAddDetailsComponent } from 'src/app/teams/team-add/team-add-details.component';
import { PercentageContainerComponent } from 'src/app/home/percentage-container/percentage-container.component';
import { NavigateDirective } from 'src/app/shared/directives/navigate.directive';
import { AutoTextResizeDirective } from 'src/app/shared/directives/auto-text-resize/auto-text-resize.directive';
import { DebounceKeyupDirective } from 'src/app/shared/directives/debounce/debounce-keyup.directive';
import { DebounceInputDirective } from 'src/app/shared/directives/debounce/debounce-input.directive';
import { MembersService } from 'src/app/shared/services/members.service';
import { EmailMessageService } from 'src/app/shared/services/email-message.service';
import { SidenavContainerComponent } from 'src/app/shared/components/sidenav-container/sidenav-container.component';
import { TeamHistoricalAddComponent } from 'src/app/teams/team-historical/team-historical-add.component';
import { MessageContentComponent } from 'src/app/admin/admin-home/message-center/content/message-center-content.component';
import { EmailCustomizationResolver } from 'src/app/shared/resolvers/email-customization.resolver';
import { AutoFocusDirective } from 'src/app/shared/directives/auto-focus.directive';
import { SurveyService } from './services/survey.service';
import { TeamFormService } from './services/team-form.service';
import { FeeAddButtonComponent } from './fee-add-button/fee-add-button.component';
import { EditableLabeledSimpleTextComponent } from './editable-labeled-simple-text/editable-labeled-simple-text.component';
import { NumberRangeEditorComponent } from './components/number-range-editor/number-range-editor.component';
import { GetPreviewPipe } from './pipes/get-preview.pipe';
import { EmbedVideoComponent } from './components/embed-video/embed-video.component';
import { SendApplicationComponent } from './components/send-application/send-application.component';
import { DateDifferencePipe } from './pipes/date-difference.pipe';
import { DaysFormattedPipe } from './pipes/days-formatted.pipe';
import { CenterMatmenuDirective } from './directives/center-matmenu.directive';
import { CustomFieldsInlineEditorComponent } from './components/custom-fields-inline-editor/custom-fields-inline-editor.component';
import { CustomFieldInlineEditorComponent } from 'src/app/shared/components/custom-fields-inline-editor/custom-field-inline-editor/custom-field-inline-editor.component';
import { ProfileCustomFieldComponent } from './components/profile-custom-field/profile-custom-field.component';
import { ODataFilterComponent } from './components/odata-filter/odata-filter.component';
import { ODataFilterItemComponent } from './components/odata-filter/odata-filter-item/odata-filter-item.component';
import { AdminAutomationOdataFilterComponent } from 'src/app/admin/admin-automation-odata-filter/admin-automation-odata-filter.component';
import { CustomViewMenuComponent } from './components/custom-view-menu/custom-view-menu.component';
import { TenantPlanErrorModalComponent } from './components/tenant-plan-error-modal/tenant-plan-error-modal.component';
import { CustomViewSidebarComponent } from './components/custom-view-sidebar/custom-view-sidebar.component';
import { PlanUpgradeBoxComponent } from './components/plan-upgrade-box/plan-upgrade-box.component';
import { BrowserReloadDialogComponent } from './components/browser-reload-dialog/browser-reload-dialog.component';
import { ManageColumnsSidebarComponent } from './components/manage-columns-sidebar/manage-columns-sidebar.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ManageColumnsComponent } from './components/manage-columns-sidebar/manage-columns/manage-columns.component';
import { PersonCardComponent } from './components/person-card/person-card.component';
import { BreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs.component';
import { QuickODataFilterComponent } from './components/quick-odata-filter/quick-odata-filter.component';
import { QuickODataFilterItemComponent } from './components//quick-odata-filter/quick-odata-filter-item/quick-odata-filter-item.component';
import { PreviewTemplateMessageComponent } from './components/preview-template-message/preview-template-message.component';
import { ManageQiuckOdataFiltersComponent } from './components/quick-odata-filter/manage-qiuck-odata-filters/manage-qiuck-odata-filters.component';
import { NgChartsModule } from 'ng2-charts';
import { ChartCanvasComponent } from './components/chart-canvas/chart-canvas.component';
import { ExpiresInPipe } from './pipes/expires-in.pipe';
import { ExportConfirmationComponent } from './components/export-confirmation/export-confirmation.component';
import { CancelReminderModalComponent } from './components/cancel-reminder-modal/cancel-reminder-modal.component';
import { EmailReminderService } from './services/email-reminder.service';
import { AceEditorComponent } from './components/ace-editor/ace-editor.component';
import { EditableLabeledRichTextComponent } from './components/editable-labeled-rich-text/editable-labeled-rich-text.component';
import { SendEmailComponent } from './components/send-email/send-email.component';
import { EmailTemplateTileComponent } from './components/email-template-tile/email-template-tile.component';
import { SeatStatusOverrideComponent } from './components/seat-status-override/seat-status-override.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { ComposeEmailComponent } from './compose-email/compose-email.component';
import { DataImportErrorModalComponent } from './components/data-import-error-modal/data-import-error-modal.component';
import { WorkspaceNamePipe } from './pipes/workspace-name.pipe';
import { EvaluationTeamRatingsPipe } from './pipes/evaluation-team-ratings.pipe';
import { EvaluationAggregatePipe } from './pipes/evaluation-aggregate.pipe';
import { PersonEvaluationDetailsComponent } from './components/person-evaluation-details/person-evaluation-details.component';
import { EnumeratedAggregateItemComponent } from './components/person-evaluation-details/enumerated-aggregate-item/enumerated-aggregate-item.component';
import { StarRatingAggregateItemComponent } from './components/person-evaluation-details/star-rating-aggregate-item/star-rating-aggregate-item.component';
import { PersonFormDetailsComponent } from './components/person-form-details/person-form-details.component';
import { CertificationDetailModalComponent } from './components/certification-detail-modal/certification-detail-modal.component';
import { FieldDataHistoricalModalComponent } from './components/field-data-historical-modal/field-data-historical-modal.component';
import { PersonWorkspaceStatusEditModalComponent } from './components/person-workspace-status-edit-modal/person-workspace-status-edit-modal.component';
import { CustomFieldsDescription } from './components/custom-fields-description/custom-fields-description.component';
import { EditExternalSystemIdModalComponent } from './components/edit-external-system-id-modal/edit-external-system-id-modal.component';
import { HistoryService } from './services/history.service';
import { TargetQuestionNamePipe } from './pipes/target-question-name.pipe';
import { OpportunityBoardService } from './services/opportunity-board.service';
import { InactiveLinkComponent } from './components/inactive-link/inactive-link.component';
import { AllowModuleDirective } from './directives/allow-module.directive';
import { CustomFieldTypeIconPipe } from './pipes/custom-field-type-icon.pipe';
import { CustomFieldTitlePipe } from './pipes/custom-field-title.pipe';
import { AutomationConditionBuilderComponent } from './components/automation-condition-builder/automation-condition-builder.component';
import { AutomationConditionBuilderItemComponent } from './components/automation-condition-builder/automation-condition-builder-item/automation-condition-builder-item.component';
import { DeleteDialogComponent } from './delete-dialog/delete-dialog.component';
import { DeleteDialogDirective } from './delete-dialog/delete-dialog.directive';
import { CustomFieldValueByIdPipe } from './pipes/custom-field-value-by-id.pipe copy';
import { FeeAdjustmentConditionPipe } from './pipes/fee-adjustment-condition.pipe';
import { RequirementFeeAdjustmentTableComponent } from './components/requirement-fee-adjustment-table/requirement-fee-adjustment-table.component';
import { SpaceCapCasePipe } from './pipes/spaceCapCase.pipe';
import {
  MonthDayHeader,
  MonthDayPickerComponent
} from './components/month-day-picker/month-day-picker.component';
import { AutomationNamePipe } from './pipes/automation-name.pipe';
import { CustomFieldUsageReportComponent } from './components/custom-field-usage-report/custom-field-usage-report.component';
import { FormPickerComponent } from './components/form-picker/form-picker.component';
import { TimeZoneComponent } from './timezone/timezone.component';
import { FieldDisplayPipe } from './pipes/field-display.pipe';
import { DisplayDataComponent } from './components/dispay-data/display-data.component';
import { CertificationSnapshotComponent } from './components/dispay-data/snap-shot/certification-snapshot.component';
import { ComplexFieldSnapshotComponent } from './components/dispay-data/snap-shot/complex-field-snapshot.component';
import { EvidenceSnapshotComponent } from './components/dispay-data/snap-shot/evidence-snapshot.component';
import { ScenarioHistorySidebarComponent } from './components/scenario-history-sidebar/scenario-history-sidebar.component';
import { CustomCircleProgressBarComponent } from './components/custom-circle-progress-bar/custom-circle-progress-bar.component';
import { CredentialApplicationReportComponent } from './components/credential-application-report/credential-application-report.component';
import { OpportunityListingDetailsComponent } from 'src/app/teams/opportunity-listing-details/opportunity-listing-details';
import { CredentialPdfEmailComponent } from './components/credential-pdf/credential-pdf-email.component';
import { NgOtpInputModule } from 'ng-otp-input';
import { ApplicationRequestVerificationComponent } from './components/application-request-verification/application-request-verification.component';
import { SharedAppPortalModule } from 'src/shared-app-portal/shared-app-portal.module';
import { CredentialPdfFileUpload } from './components/credential-pdf-upload/credential-pdf-file-upload.component';
import { BulkUpdateStatusWorkspacesModalComponent } from './components/bulk-update-status-workspaces-modal/bulk-update-status-workspaces-modal.component';
import { EvidenceSnapshotsComponent } from './components/dispay-data/snap-shot/evidence-snapshots.component';
import { RecentUpdatesModalComponent } from 'src/app/shared/components/recent-updates-modal/recent-updates-modal.component';
import { CredentialHistoryComponent } from './components/credential-history/credential-history.component';
import { YesNoPipe } from './pipes/yesno-pipe';
import { SpaceCapCaseToLowerPipe } from './pipes/spaceCapCaseToLower.pipe';
import { RequirementFeeFormsComponent } from './components/requirement-fee-forms/requirement-fees-form.component';
import { TeamWorkspaceMigrationComponent } from '../teams/team-workspace-migration/team-workspace-migration.component';
import { MemberLogoNameComponent } from './components/member-logo-name/member-logo-name.component';
import { MembersHeaderControlsComponent } from './components/members-header-controls/members-header-controls.component';
import { ColorPickerComponent } from './components/color-picker/color-picker.component';
import { SeatStatusEditModalComponent } from './components/seat-status-edit-modal/seat-status-edit-modal.component';
import { SeatDateEditModalComponent } from './components/seat-date-edit-modal/seat-date-edit-modal.component';
import { CleanUrlPipe } from 'src/shared-app-portal/pipes/clean-url.pipe';
import { PersonAutoCompleteComponent } from './components/person-autocomplete/person-autocomplete.component';
import { NamePillComponent } from './components/name-pill/name-pill.component';

@NgModule({
  imports: [
    SharedAppPortalModule,
    CommonModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    DragDropModule,
    NgChartsModule,
    NgOtpInputModule
  ],
  exports: [
    FeeAddButtonComponent,
    ExportConfirmationComponent,
    SharedAppPortalModule,
    CustomFieldsInlineEditorComponent,
    CustomFieldInlineEditorComponent,
    ProfileCustomFieldComponent,
    SendApplicationComponent,
    SearchBarComponent,
    OrderByPipe,
    currentOrUpcomingTeamsSummaryPipe,
    CheckboxGroup,
    TeamHeaderComponent,
    TeamsNavComponent,
    FilterByPipe,
    FieldDisplayPipe,
    PositionDetailsComponent,
    SidenavContainerComponent,
    NavigateDirective,
    AutoTextResizeDirective,
    DebounceKeyupDirective,
    DebounceInputDirective,
    PercentageContainerComponent,
    DeleteDialogDirective,
    DeleteDialogComponent,
    RecaptchaModule,
    RecaptchaFormsModule,
    EditableLabeledSimpleTextComponent,
    NumberRangeEditorComponent,
    GetPreviewPipe,
    EmbedVideoComponent,
    YesNoPipe,
    DateDifferencePipe,
    DaysFormattedPipe,
    CleanUrlPipe,
    CenterMatmenuDirective,
    ODataFilterComponent,
    CustomViewMenuComponent,
    CustomViewSidebarComponent,
    PlanUpgradeBoxComponent,
    BrowserReloadDialogComponent,
    ManageColumnsSidebarComponent,
    ManageColumnsComponent,
    PersonCardComponent,
    BreadcrumbsComponent,
    ODataFilterItemComponent,
    AdminAutomationOdataFilterComponent,
    QuickODataFilterComponent,
    QuickODataFilterItemComponent,
    PreviewTemplateMessageComponent,
    ManageQiuckOdataFiltersComponent,
    NgChartsModule,
    ChartCanvasComponent,
    ExpiresInPipe,
    CancelReminderModalComponent,
    AceEditorComponent,
    EditableLabeledRichTextComponent,
    SendEmailComponent,
    EmailTemplateTileComponent,
    SeatStatusOverrideComponent,
    PaginatorComponent,
    ComposeEmailComponent,
    WorkspaceNamePipe,
    EvaluationTeamRatingsPipe,
    EvaluationAggregatePipe,
    PersonEvaluationDetailsComponent,
    StarRatingAggregateItemComponent,
    EnumeratedAggregateItemComponent,
    PersonFormDetailsComponent,
    EditExternalSystemIdModalComponent,
    TargetQuestionNamePipe,
    InactiveLinkComponent,
    AllowModuleDirective,
    CustomFieldTypeIconPipe,
    CustomFieldTitlePipe,
    AutomationConditionBuilderComponent,
    CustomFieldValueByIdPipe,
    ScenarioHistorySidebarComponent,
    CredentialHistoryComponent,
    FeeAdjustmentConditionPipe,
    RequirementFeeAdjustmentTableComponent,
    SpaceCapCasePipe,
    SpaceCapCaseToLowerPipe,
    MonthDayPickerComponent,
    MonthDayHeader,
    AutomationNamePipe,
    CustomFieldUsageReportComponent,
    FormPickerComponent,
    TimeZoneComponent,
    DisplayDataComponent,
    CertificationSnapshotComponent,
    ComplexFieldSnapshotComponent,
    EvidenceSnapshotComponent,
    EvidenceSnapshotsComponent,
    CustomCircleProgressBarComponent,
    CredentialApplicationReportComponent,
    OpportunityListingDetailsComponent,
    CredentialPdfEmailComponent,
    ApplicationRequestVerificationComponent,
    CredentialPdfFileUpload,
    RequirementFeeFormsComponent,
    TeamWorkspaceMigrationComponent,
    MemberLogoNameComponent,
    ColorPickerComponent,
    SeatStatusEditModalComponent,
    SeatDateEditModalComponent,
    PersonAutoCompleteComponent,
    AutoFocusDirective,
    NamePillComponent
  ],
  declarations: [
    ColorPickerComponent,
    FeeAddButtonComponent,
    MembersHeaderControlsComponent,
    MemberLogoNameComponent,
    CustomFieldInlineEditorComponent,
    CustomFieldsInlineEditorComponent,
    ProfileCustomFieldComponent,
    SendApplicationComponent,
    TeamHeaderComponent,
    SearchBarComponent,
    CheckboxGroup,
    TeamsNavComponent,
    OrderByPipe,
    currentOrUpcomingTeamsSummaryPipe,
    FilterByPipe,
    FieldDisplayPipe,
    PositionDetailsComponent,
    PreviewApplicationModalComponent,
    DeclineReasonModalComponent,
    DeletePersonModalComponent,
    DeletePersonWorkspaceModalComponent,
    DeleteWorkspaceModalComponent,
    TeamAddComponent,
    TeamAddDetailsComponent,
    SidenavContainerComponent,
    TeamHistoricalAddComponent,
    NavigateDirective,
    AutoFocusDirective,
    AutoTextResizeDirective,
    DebounceKeyupDirective,
    DebounceInputDirective,
    PercentageContainerComponent,
    DeleteDialogDirective,
    DeleteDialogComponent,
    MessageContentComponent,
    EditableLabeledSimpleTextComponent,
    NumberRangeEditorComponent,
    GetPreviewPipe,
    EmbedVideoComponent,
    YesNoPipe,
    DateDifferencePipe,
    DaysFormattedPipe,
    CleanUrlPipe,
    CenterMatmenuDirective,
    ODataFilterComponent,
    ODataFilterItemComponent,
    AdminAutomationOdataFilterComponent,
    CustomViewMenuComponent,
    TenantPlanErrorModalComponent,
    CustomViewSidebarComponent,
    PlanUpgradeBoxComponent,
    BrowserReloadDialogComponent,
    ManageColumnsSidebarComponent,
    ManageColumnsComponent,
    PersonCardComponent,
    BreadcrumbsComponent,
    QuickODataFilterComponent,
    QuickODataFilterItemComponent,
    PreviewTemplateMessageComponent,
    ManageQiuckOdataFiltersComponent,
    ChartCanvasComponent,
    ExpiresInPipe,
    ExportConfirmationComponent,
    CancelReminderModalComponent,
    AceEditorComponent,
    EditableLabeledRichTextComponent,
    SendEmailComponent,
    EmailTemplateTileComponent,
    SeatStatusOverrideComponent,
    PaginatorComponent,
    ComposeEmailComponent,
    DataImportErrorModalComponent,
    WorkspaceNamePipe,
    EvaluationTeamRatingsPipe,
    EvaluationAggregatePipe,
    PersonEvaluationDetailsComponent,
    StarRatingAggregateItemComponent,
    EnumeratedAggregateItemComponent,
    PersonFormDetailsComponent,
    CertificationDetailModalComponent,
    FieldDataHistoricalModalComponent,
    PersonWorkspaceStatusEditModalComponent,
    CustomFieldsDescription,
    EditExternalSystemIdModalComponent,
    TargetQuestionNamePipe,
    InactiveLinkComponent,
    AllowModuleDirective,
    CustomFieldTypeIconPipe,
    CustomFieldTitlePipe,
    AutomationConditionBuilderComponent,
    AutomationConditionBuilderItemComponent,
    CustomFieldValueByIdPipe,
    CredentialHistoryComponent,
    ScenarioHistorySidebarComponent,
    FeeAdjustmentConditionPipe,
    RequirementFeeAdjustmentTableComponent,
    SpaceCapCasePipe,
    SpaceCapCaseToLowerPipe,
    MonthDayPickerComponent,
    MonthDayHeader,
    AutomationNamePipe,
    CustomFieldUsageReportComponent,
    FormPickerComponent,
    TimeZoneComponent,
    DisplayDataComponent,
    CertificationSnapshotComponent,
    ComplexFieldSnapshotComponent,
    EvidenceSnapshotComponent,
    EvidenceSnapshotsComponent,
    CustomCircleProgressBarComponent,
    CredentialApplicationReportComponent,
    OpportunityListingDetailsComponent,
    CredentialPdfEmailComponent,
    ApplicationRequestVerificationComponent,
    CredentialPdfFileUpload,
    RequirementFeeFormsComponent,
    BulkUpdateStatusWorkspacesModalComponent,
    RecentUpdatesModalComponent,
    TeamWorkspaceMigrationComponent,
    SeatStatusEditModalComponent,
    SeatDateEditModalComponent,
    PersonAutoCompleteComponent,
    NamePillComponent
  ]
})
export class SharedModule {
  lastValue;
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        SearchModeService,
        TagService,
        AuthGuardService,
        ProductionGuardService,
        StagingGuardService,
        AWSService,
        {
          provide: ErrorHandler,
          useClass: GlobalErrorHandler
        },
        MembersService,
        EmailMessageService,
        EmailCustomizationResolver,
        SurveyService,
        TeamFormService,
        EmailReminderService,
        DatePipe,
        HistoryService,
        OpportunityBoardService
      ]
    };
  }

  public constructor(m: MediaMarshaller) {
    // hack until resolve: https://github.com/angular/flex-layout/issues/1201
    // @ts-ignore
    m.subject.subscribe(() => {
      // @ts-ignore
      if (m.activatedBreakpoints.filter((b) => b.alias === 'print').length === 0) {
        // @ts-ignore
        this.lastValue = [...m.activatedBreakpoints];
      } else {
        // @ts-ignore
        m.activatedBreakpoints = [...this.lastValue];
        // @ts-ignore
        m.hook.collectActivations = () => {};
        // @ts-ignore
        m.hook.deactivations = [...this.lastValue];
      }
    });
  }
}
